<template>
  <teleport to="body">
    <Html :style="show ? 'overflow: hidden; max-height: 100vh; height: 100vh;' : 'overflow: auto'" />
    <section class="popup" v-if="show" style="margin: auto">
      <div v-if="success" class="form_success" ref="formSuccess">
        <p class="success">{{ t("booking.request_sent") }}</p>
        <div class="close-popup" @click="closePopup"></div>
      </div>
      <div v-else class="form" ref="form">
        <div class="content">
          <div class="logo">
            <img alt="Hotel Logo" src="/img/logo_small.svg" />
          </div>
          <div class="h">{{ t("booking.plan_arrival") }}</div>
          <form @submit.prevent="submitOrder" class="booking-form grid grid-cols-2" novalidate>
            <div class="booking-form__fields">
              <div
                class="mdl-textfield mdl-js-textfield mdl-textfield--floating-label txt-full-width is-upgraded is-dirty relative"
                :class="{ 'has-error': errors.name }"
                data-upgraded="MaterialTextfield">
                <input class="mdl-textfield__input" type="text" :value="name" @input="onNameInput" />
                <label class="mdl-textfield__label" :class="[name.length ? ' position_label' : '']">{{ t("booking.name")
                  }}</label>
                <p v-if="errors.name" class="field-error">{{ errors.name }}</p>
              </div>
              <div
                class="mdl-textfield mdl-js-textfield mdl-textfield--floating-label txt-full-width is-upgraded is-dirty relative"
                :class="{ 'has-error': errors.email }">
                <input class="mdl-textfield__input" type="email" :value="email" @input="onEmailInput" />
                <label class="mdl-textfield__label" :class="[email.length ? ' position_label' : '']">{{
                  t("booking.email")
                }}
                </label>
                <p v-if="errors.email" class="field-error">{{ errors.email }}</p>
              </div>
              <div
                class="mdl-textfield mdl-js-textfield mdl-textfield--floating-label txt-full-width is-upgraded is-dirty relative"
                :class="{ 'has-error': errors.phone }">
                <input class="mdl-textfield__input" type="tel" :value="phone" name="phone" @input="onPhoneInput" />
                <label class="mdl-textfield__label" :class="[phone ? ' position_label' : '']">{{ t("booking.phone") }}
                </label>
                <p v-if="errors.phone" class="field-error">{{ errors.phone }}</p>
              </div>
              <div
                class="mdl-textfield mdl-js-textfield mdl-textfield--floating-label txt-full-width is-upgraded is-dirty relative z-[500] mt-1"
                :class="{ 'has-error': errors.dates }">
                <span class="date" :class="[order.checkIn ? ' date' : 'none_date']">{{ t("booking.stay") }}</span>
                <HotelDatePicker class="z-[500]" @confirm="submitOrder"></HotelDatePicker>
                <p v-if="errors.dates" class="field-error">{{ errors.dates }}</p>
              </div>
              <div class="mdl-textfield submit-field">
                <p v-if="formError" class="field-error form-error">{{ formError }}</p>
                <button type="submit" class="mdl-textfield__button cursor-pointer" :disabled="submitting">
                  {{ t("booking.submit") }}
                </button>
              </div>
            </div>
          </form>
        </div>
        <div class="close-popup" @click="closePopup" role="button" aria-label="Close"></div>
      </div>
    </section>
  </teleport>
</template>

<i18n lang="json">{
  "en": {
    "booking": {
      "request_sent": "Request sent",
      "plan_arrival": "Plan your arrival and we will call you back",
      "name": "Name",
      "stay": "Stay",
      "phone": "Phone",
      "email": "Email",
      "submit": "Book",
      "name_error": "Enter your name",
      "email_error": "Enter a valid email",
      "phone_error": "Invalid phone number. Examples: +79409363634; (123)456-7890; 075-63546725; 123-456-7890",
      "dates_error": "Select check-in and check-out dates",
      "submit_error": "Could not send the request. Try again."
    }
  },
  "ru": {
    "booking": {
      "request_sent": "Заявка отправлена",
      "plan_arrival": "Запланируйте свой приезд и мы Вам перезвоним",
      "name": "Имя",
      "stay": "Сроки пребывания",
      "phone": "Телефон",
      "email": "Email",
      "submit": "Забронировать",
      "name_error": "Укажите имя",
      "email_error": "Укажите корректный email",
      "phone_error": "Номер телефона введен некорректно. Примеры: +79409363634; (123)456-7890; 075-63546725; 123-456-7890",
      "dates_error": "Выберите даты заезда и выезда",
      "submit_error": "Не удалось отправить заявку. Попробуйте ещё раз."
    }
  }
}</i18n>

<script setup>
import { onClickOutside } from "@vueuse/core"

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_RE = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/

const form = ref(null)
const formSuccess = ref(null)

onClickOutside(form, () => {
  closePopup()
})

onClickOutside(formSuccess, () => {
  closePopup()
})

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(["close"])
const ordersStore = useOrdersStore()
const { t } = useI18n()

const success = ref(false)
const submitting = ref(false)
const formError = ref("")
const errors = reactive({
  name: "",
  email: "",
  phone: "",
  dates: ""
})

const name = computed(() => ordersStore.item.name)
const email = computed(() => ordersStore.item.email)
const phone = computed(() => ordersStore.item.phone)
const order = computed(() => ordersStore.item)

function clearErrors() {
  errors.name = ""
  errors.email = ""
  errors.phone = ""
  errors.dates = ""
  formError.value = ""
}

function isValidPhone(value) {
  const trimmed = value.trim()
  if (!PHONE_RE.test(trimmed)) return false
  const digits = trimmed.replace(/\D/g, "")
  return digits.length >= 7 && digits.length <= 15
}

function validateForm() {
  clearErrors()
  let ok = true

  if (!name.value?.trim()) {
    errors.name = t("booking.name_error")
    ok = false
  }

  if (!email.value?.trim() || !EMAIL_RE.test(email.value.trim())) {
    errors.email = t("booking.email_error")
    ok = false
  }

  if (!phone.value?.trim() || !isValidPhone(phone.value)) {
    errors.phone = t("booking.phone_error")
    ok = false
  }

  if (!order.value.checkIn || !order.value.checkOut) {
    errors.dates = t("booking.dates_error")
    ok = false
  }

  return ok
}

const onNameInput = (event) => {
  ordersStore.setName(event.target.value)
  errors.name = ""
}

const onPhoneInput = (event) => {
  ordersStore.setPhone(event.target.value)
  errors.phone = ""
}

const onEmailInput = (event) => {
  ordersStore.setEmail(event.target.value)
  errors.email = ""
}

watch(
  () => [order.value.checkIn, order.value.checkOut],
  () => {
    if (order.value.checkIn && order.value.checkOut) errors.dates = ""
  }
)

const closePopup = () => {
  if (props.show) {
    emit("close")
    ordersStore.setSuccess(false)
    ordersStore.setRoom("")
    ordersStore.setName("")
    ordersStore.setCheckIn(null)
    ordersStore.setCheckOut(null)
    ordersStore.setEmail("")
    ordersStore.setPhone("")
  }

  clearErrors()
  success.value = false
  submitting.value = false
}

async function submitOrder() {
  if (!validateForm() || submitting.value) return

  submitting.value = true
  formError.value = ""

  try {
    await $fetch("https://api.leonhotel.ru/api/v1/orders", {
      method: "POST",
      body: {
        name: ordersStore.item.name.trim(),
        phone: ordersStore.item.phone.trim(),
        email: ordersStore.item.email.trim(),
        room: ordersStore.item.room,
        checkIn: ordersStore.item.checkIn,
        checkOut: ordersStore.item.checkOut
      }
    })
    success.value = true
  } catch (err) {
    formError.value = t("booking.submit_error")
  } finally {
    submitting.value = false
  }
}

onBeforeUnmount(() => {
  ordersStore.setSuccess(false)
  ordersStore.setRoom("")
  ordersStore.setName("")
  ordersStore.setCheckIn(null)
  ordersStore.setCheckOut(null)
  ordersStore.setEmail("")
  ordersStore.setPhone("")
  success.value = false
})
</script>

<style scoped>
.popup {
  display: flex;
  background-color: rgba(0, 0, 0, 0.58);
  position: fixed !important;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 100500;
  position: relative;
}

.top {
  margin-top: 40px;
}

.bottom {
  margin-top: 15px;
}

.popup .form {
  width: 1170px;
  height: 517px;
  background-color: white;
  margin: auto;
  position: relative;
  display: flex;
  padding: 57px;
  z-index: 4;
}

.popup .form_success {
  width: 1170px;
  height: 450px;
  background-color: white;
  margin: auto;
  position: relative;
  padding: 57px;
  z-index: 4;
}

.success {
  font-family: "Lato";
  font-size: 35px;
  text-align: center;
  margin-top: 12rem;
  line-height: 24px;
}

.popup .form .borders {
  position: absolute;
  left: 55px;
  right: 55px;
  top: 55px;
  bottom: 55px;
}

.popup .form .content {
  position: relative;
  width: 100%;
  max-width: 100%;
  flex: (0, 0, 100%);
}

.popup .form .content .h {
  font-family: "Lato";
  font-size: 22px;
  text-align: center;
  margin-top: 66px;
  margin-bottom: 4px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  top: -12px;
}

.popup .form .content form {
  width: 790px;
  margin-left: auto;
  margin-right: auto;
  margin-top: 3rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}

.popup .form .content form .booking-form__fields {
  display: contents;
}

.popup .form .content form div {
  max-width: 340px;
  flex: (0 0 340px);
}

.mdl-textfield {
  padding: 0px;
  padding-top: 20px;
  margin-top: 15px;
  z-index: 5;
}

.popup .form .content form .mdl-textfield .mdl-textfield__input {
  border-bottom: 1px solid #707070 !important;
  font-family: "Lato";
  font-size: 16px;
  outline: none;
}

.popup .form .content form .mdl-textfield .mdl-textfield__label {
  color: #8d8d8d;
  font-family: "Lato" !important;
  font-size: 16px !important;
  top: 20px;
}

.mb {
  margin-bottom: 19px;
}

.position_label {
  margin-top: -33px;
}

form input {
  padding-bottom: 1rem;
}

input:focus~label,
label:active {
  margin-top: -33px;
  transition: all 0.3s ease-in-out;
}

.date {
  color: #8d8d8d;
  font-family: "Lato" !important;
  font-size: 16px !important;
  position: absolute;
  top: -2px;
  transition: all 0.3s ease-in-out;
}

.none_date {
  display: none;
}

.popup .form .content form .mdl-textfield .mdl-textfield__button {
  color: black !important;
  font-family: "Lato" !important;
  font-size: 16px !important;
  height: 50px;
  width: 236px;
  margin-top: 7px;
  margin-top: 3rem;
  background-color: #d9ccaf;
  display: block;
  margin-left: auto;
  border: none !important;
  font-family: "Lato";
  font-size: 16px;
}

.popup .close-popup {
  position: absolute;
  right: -26px;
  top: -28px;
  background-image: url("/img/close-popup.svg");
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  cursor: pointer;
  height: 45px;
  width: 45px;
  z-index: 10;
  flex: none !important;
  max-width: 45px !important;
}

.field-error {
  color: #c0392b;
  font-family: "Lato", sans-serif;
  font-size: 12px;
  line-height: 1.3;
  margin: 6px 0 0;
}

.form-error {
  margin-bottom: 10px;
}

.has-error .mdl-textfield__input,
.has-error .datepicker__dummy-wrapper {
  border-bottom-color: #c0392b !important;
}

.popup .form .content form .mdl-textfield :deep(.datepicker__wrapper) {
  display: block;
  float: none;
  width: 100%;
  max-width: 100%;
  height: auto;
  min-height: 44px;
}

.popup .form .content form .mdl-textfield :deep(.datepicker__dummy-wrapper) {
  display: flex;
  float: none;
  width: 100%;
  max-width: 100%;
  height: auto;
  min-height: 44px;
}

.popup .logo {
  position: absolute;
  left: -32px;
  top: -20px;
  height: 69px;
  width: 120px;
  background-color: white;
  padding-left: 16px;
  padding-right: 16px;
}

.popup .logo img {
  height: 100%;
  filter: invert(1);
}

@media screen and (max-width: 900px) {
  .popup {
    overflow: hidden;
  }

  .popup .form .borders {
    display: none;
  }

  .popup .logo {
    left: 0;
    right: 0;
    margin: auto;
    top: 35px;
  }

  .popup .form .content .h {
    font-size: 20px;
    margin-top: 145px;
    padding: 0px 35px;
    line-height: 24px;
  }

  .popup .form {
    width: 100vw;
    height: 100vh;
    background-color: white;
    margin: 0;
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 0;
    z-index: 4;
    top: 0;
    left: 0;
    overflow: hidden;
  }

  .popup .form .content {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .popup .close-popup {
    position: absolute;
    right: 15px;
    top: 15px;
    font-size: 50px;
    background-image: url(/img/close-popup.svg);
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    cursor: pointer;
    height: 51px;
    width: 51px;
    max-width: 51px !important;
    z-index: 4;
  }

  .popup .form .content form.booking-form {
    width: 100%;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    justify-content: flex-start;
  }

  .popup .form .content form .booking-form__fields {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 0 26px 24px;
    width: 100%;
    max-width: 100%;
  }

  .mb {
    margin-bottom: 0;
  }

  .mdl-textfield {
    margin-bottom: 13px;
  }

  .popup .form .content form .submit-field {
    width: 100%;
    max-width: 100%;
    margin: 1.5rem 0 0;
    padding: 0;
    background: transparent;
    box-shadow: none;
  }

  .popup .form .content form:has(.datepicker--open) .submit-field {
    display: none;
  }

  .popup .form .content form .mdl-textfield .mdl-textfield__button {
    margin: 0;
    margin-top: 0;
    width: 100%;
    max-width: 100%;
  }

  .popup .form .content form .booking-form__fields > div {
    max-width: 100%;
    flex: 0 0 auto;
    width: 100%;
  }
}

@media screen and (max-width: 520px) {
  .popup .form .content .h {
    padding: 0px 45px;
  }
}

@media screen and (max-width: 425px) {
  .popup .form .content .h {
    padding: 0px 55px;
  }
}

.success {
  margin-top: 14rem;
  font-size: 30px;
  line-height: 30px;
}
</style>

<style scoped>
.popup .form .content form input {
  outline: none;
}
</style>
